import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Money } from '@mlm/ui';
import { listDistributedIncome, type DistributedIncomeRow } from '../lib/api';

/**
 * Phase 12C.4 — Distributed Income (admin) drilldown. Lists every
 * (plan, event) run from `comp_plan_runs`, joined with the ledger rows
 * each one produced. Filterable by user / slot / plan / time window.
 * Drill INTO a row to see every recipient + amount.
 */
export function DistributionsPage() {
  const [planCode, setPlanCode] = useState('');
  const [userId, setUserId] = useState('');
  const [slotId, setSlotId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openRow, setOpenRow] = useState<string | null>(null);

  const params: Parameters<typeof listDistributedIncome>[0] = {
    planCode: planCode || undefined,
    userId: userId || undefined,
    slotId: slotId || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
    limit: 200,
  };
  const q = useQuery({
    queryKey: ['distributions', params],
    queryFn: () => listDistributedIncome(params),
  });

  const rowKey = (r: DistributedIncomeRow) => `${r.eventId}|${r.planCode}`;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Distributed Income</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Every comp event the framework ran, with the ledger rows it
            produced. Click a row to expand.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Input
              placeholder="plan code"
              value={planCode}
              onChange={(e) => setPlanCode(e.target.value)}
            />
            <Input
              placeholder="user id"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            <Input
              placeholder="slot id"
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
            />
            <Input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <Input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => q.refetch()}>
              Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setPlanCode('');
                setUserId('');
                setSlotId('');
                setFrom('');
                setTo('');
              }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !q.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No runs match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium w-8"></th>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Plan</th>
                  <th className="px-4 py-2 font-medium">Event</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Entries</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((r) => {
                  const k = rowKey(r);
                  const isOpen = openRow === k;
                  return (
                    <>
                      <tr
                        key={k}
                        className="border-b last:border-0 cursor-pointer hover:bg-muted/40"
                        onClick={() => setOpenRow(isOpen ? null : k)}
                      >
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {isOpen ? '▾' : '▸'}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {r.startedAt ? new Date(r.startedAt).toLocaleString() : ''}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{r.planCode}</td>
                        <td className="px-4 py-2 text-xs">
                          {r.eventType} <span className="opacity-50">{r.eventId.slice(-12)}</span>
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={
                              r.status === 'succeeded'
                                ? 'text-success text-xs font-medium'
                                : r.status === 'failed'
                                  ? 'text-destructive text-xs font-medium'
                                  : 'text-muted-foreground text-xs'
                            }
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-xs">{r.entries.length}</td>
                      </tr>
                      {isOpen ? (
                        <tr key={`${k}-detail`} className="bg-muted/20 border-b">
                          <td colSpan={6} className="px-8 py-3">
                            {r.error ? (
                              <p className="text-xs text-destructive mb-2">
                                Error: {r.error}
                              </p>
                            ) : null}
                            {r.entries.length === 0 ? (
                              <p className="text-xs text-muted-foreground">
                                No ledger entries (compute returned empty or
                                everything was a duplicate).
                              </p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead className="text-muted-foreground">
                                  <tr>
                                    <th className="text-left py-1">Slot</th>
                                    <th className="text-left py-1">Kind</th>
                                    <th className="text-left py-1">Level</th>
                                    <th className="text-right py-1">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.entries.map((e, i) => (
                                    <tr key={i}>
                                      <td className="py-0.5 text-xs">
                                        <span className="font-mono">
                                          {e.slotCode ?? `${e.slotId.slice(0, 8)}…`}
                                        </span>
                                        {e.ownerName ? (
                                          <span className="text-muted-foreground"> · {e.ownerName}</span>
                                        ) : null}
                                      </td>
                                      <td className="py-0.5">{e.kind}</td>
                                      <td className="py-0.5">{e.level ?? ''}</td>
                                      <td className="py-0.5 text-right">
                                        <Money
                                          amount={e.amount}
                                          currency={e.currencyKind}
                                          direction="credit"
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
